/**
 * The PWA / browser adapter.
 *
 * Prints by putting the finished HTML into a detached iframe and calling
 * `print()` on its window. The font guard runs first — see `printDocument.ts`
 * for why that check cannot be skipped and why it must be given the IFRAME's
 * fonts rather than the parent's.
 *
 * `savePdf` deliberately REFUSES. The browser has no way to write a PDF to a
 * chosen path without a JS library that would re-implement layout and destroy
 * the millimetre geometry this project is built around. The only honest PDF
 * route here is the user choosing "Save as PDF" in the print dialog, and the UI
 * is told to offer exactly that.
 */
import { printHtml, type PrintEnvironment } from './printDocument';
import {
  PrintPortError,
  type PrintCapabilities,
  type PrintPort,
  type PrintRequest,
  type PrintResult,
  type PdfRequest,
  type PdfResult,
} from './printPort';

export const WEB_CAPABILITIES: PrintCapabilities = {
  platform: 'web',
  interactivePrint: true,
  silentPdf: false,
  directThermal: false,
};

export interface BrowserPrintOptions {
  /** Directory the TTFs are served from, with a trailing slash. */
  readonly assetsBaseUrl?: string;
  /** Injected for tests; defaults to the real document. */
  readonly hostDocument?: Document;
  /**
   * Overrides the frame's font environment.
   *
   * `printHtml` reads the IFRAME's `document.fonts`, because the parent has no
   * DocSans and `check()` reports an undeclared family as ready. Hosts that
   * supply their own frame - and test environments, where happy-dom has no
   * FontFaceSet at all - need to supply that here.
   */
  readonly fontEnvironment?: Partial<PrintEnvironment>;
}

export function createBrowserPrintPort(opt: BrowserPrintOptions = {}): PrintPort {
  return {
    capabilities: WEB_CAPABILITIES,

    async print(request: PrintRequest): Promise<PrintResult> {
      const host = opt.hostDocument ?? globalThis.document;
      if (!host) {
        throw new PrintPortError('PRINT_FAILED', 'No document is available to print into.');
      }
      await printHtml(request.document.html, host, opt.fontEnvironment ?? {});
      // The dialog is modal and returns immediately on dismissal; the browser
      // gives no reliable "did they print" answer, so the honest result is that
      // the request was delivered.
      return { via: 'browser-dialog', printed: true };
    },

    async savePdf(_request: PdfRequest): Promise<PdfResult> {
      throw new PrintPortError(
        'PDF_UNAVAILABLE_ON_WEB',
        'A browser cannot save a PDF to a chosen path without re-implementing layout, ' +
          'which would break the page geometry. Use Print, then choose "Save as PDF" ' +
          'in the destination dropdown. On the Windows desktop app this button saves ' +
          'directly.',
      );
    },
  };
}